import React from 'react';
import { motion } from 'framer-motion';
import { Heart, ArrowUp, Github, Linkedin, Mail, Terminal, ExternalLink } from 'lucide-react';

const Footer: React.FC = () => {
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const quickLinks = [
    { name: 'About', href: '#about' },
    { name: 'Projects', href: '#projects' },
    { name: 'Skills', href: '#skills' },
    { name: 'Certifications', href: '#certifications' },
    { name: 'Contact', href: '#contact' },
  ];

  const socialLinks = [
    { name: 'GitHub', icon: <Github size={16} />, href: 'https://github.com/amitadhikary02', color: '#a8d8ff' },
    { name: 'LinkedIn', icon: <Linkedin size={16} />, href: 'https://linkedin.com/in/amit-adhikary-02', color: '#00ffee' },
    { name: 'Email', icon: <Mail size={16} />, href: 'mailto:amitadhikary0604@gmail.com', color: '#00ff9f' },
  ];

  const techStack = ['React', 'TypeScript', 'Tailwind CSS', 'Framer Motion'];

  return (
    <footer className="relative pt-16 pb-8 overflow-hidden" style={{ background: '#ffffff', fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
      {/* Top border line */}
      <div
        className="absolute top-0 left-0 right-0 h-px"
        style={{ background: 'linear-gradient(90deg, transparent, rgba(0,0,0,0.1), rgba(0,0,0,0.05), transparent)' }}
      />

      {/* Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute inset-0 opacity-40"
          style={{
            background: 'radial-gradient(circle at 50% 0%, rgba(0,0,0,0.03) 0%, transparent 70%)',
          }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Main grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12 mb-12">

          {/* Brand */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="lg:col-span-2"
          >
            <div className="flex items-center gap-3 mb-4">
              <div
                className="w-10 h-10 rounded-2xl flex items-center justify-center"
                style={{
                  background: 'rgba(0, 0, 0, 0.05)',
                  border: '1px solid rgba(0, 0, 0, 0.1)',
                }}
              >
                <Terminal size={18} style={{ color: '#0f172a' }} />
              </div>
              <div>
                <h3
                  className="text-base font-bold text-[#0f172a]"
                  style={{ letterSpacing: '-0.02em' }}
                >
                  Amit Adhikary
                </h3>
                <p className="text-xs text-[#475569]">
                  Full Stack Developer
                </p>
              </div>
            </div>

            <p
              className="text-sm leading-relaxed max-w-sm mb-6 text-[#475569]"
            >
              Passionate about creating elegant solutions to complex problems. Transforming ideas into reality through clean code and thoughtful design.
            </p>

            {/* Social icons */}
            <div className="flex items-center gap-2">
              {socialLinks.map((s, i) => (
                <motion.a
                  key={s.name}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, scale: 0 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ delay: i * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  className="w-10 h-10 rounded-2xl flex items-center justify-center transition-all duration-300"
                  style={{
                    background: 'rgba(0, 0, 0, 0.03)',
                    border: '1px solid rgba(0, 0, 0, 0.08)',
                    color: '#475569',
                  }}
                  onMouseEnter={e => {
                    (e.currentTarget as HTMLElement).style.color = '#0f172a';
                    (e.currentTarget as HTMLElement).style.background = 'rgba(0, 0, 0, 0.08)';
                    (e.currentTarget as HTMLElement).style.borderColor = 'rgba(0, 0, 0, 0.2)';
                  }}
                  onMouseLeave={e => {
                    (e.currentTarget as HTMLElement).style.color = '#475569';
                    (e.currentTarget as HTMLElement).style.background = 'rgba(0, 0, 0, 0.03)';
                    (e.currentTarget as HTMLElement).style.borderColor = 'rgba(0, 0, 0, 0.08)';
                  }}
                  aria-label={s.name}
                >
                  {s.icon}
                </motion.a>
              ))}
            </div>
          </motion.div>

          {/* Quick links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            viewport={{ once: true }}
          >
            <h4
              className="text-xs font-semibold mb-5 text-[#475569] uppercase tracking-wider"
            >
              Navigation
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((link, i) => (
                <motion.li
                  key={link.name}
                  initial={{ opacity: 0, x: -8 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + i * 0.05 }}
                  viewport={{ once: true }}
                >
                  <a
                    href={link.href}
                    className="flex items-center gap-2 text-sm text-[#475569] transition-colors duration-200 group hover:text-[#0f172a]"
                  >
                    <span
                      className="w-1.5 h-1.5 rounded-full flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity bg-[#0f172a]"
                    />
                    {link.name}
                  </a>
                </motion.li>
              ))}
            </ul>
          </motion.div>

          {/* Built with */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <h4
              className="text-xs font-semibold mb-5 text-[#475569] uppercase tracking-wider"
            >
              Built With
            </h4>
            <div className="space-y-3">
              {techStack.map((tech, i) => (
                <motion.div
                  key={tech}
                  initial={{ opacity: 0, x: -8 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2 + i * 0.06 }}
                  viewport={{ once: true }}
                  className="flex items-center gap-3 text-sm text-[#475569]"
                >
                  <div
                    className="w-1.5 h-1.5 rounded-full flex-shrink-0 bg-slate-400"
                  />
                  <span>
                    {tech}
                  </span>
                </motion.div>
              ))}
            </div>

            {/* Portfolio link */}
            <motion.a
              href="https://amitadhikary.com"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ x: 3 }}
              className="flex items-center gap-1.5 mt-6 text-sm text-[#475569] hover:text-[#0f172a] transition-colors duration-200"
            >
              <ExternalLink size={14} />
              amitadhikary.com
            </motion.a>
          </motion.div>
        </div>

        {/* Divider */}
        <motion.div
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: 1 }}
          transition={{ duration: 0.9 }}
          viewport={{ once: true }}
          className="h-px mb-6"
          style={{ background: 'linear-gradient(90deg, transparent, rgba(0,0,0,0.1), rgba(0,0,0,0.05), transparent)' }}
        />

        {/* Bottom bar */}
        <div 
          className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 rounded-3xl"
          style={{
            background: 'rgba(0, 0, 0, 0.02)',
            border: '1px solid rgba(0, 0, 0, 0.05)',
            backdropFilter: 'blur(10px)',
          }}
        >
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            viewport={{ once: true }}
            className="flex flex-col sm:flex-row items-center gap-2 sm:gap-6"
          >
            <p className="text-sm text-[#475569] text-center sm:text-left">
              © {new Date().getFullYear()} Amit Adhikary. All rights reserved.
            </p>
            <p className="text-sm flex items-center gap-1.5 justify-center sm:justify-start text-[#475569]">
              Crafted with
              <motion.span animate={{ scale: [1, 1.3, 1] }} transition={{ duration: 1.5, repeat: Infinity }}>
                <Heart size={14} className="fill-current text-red-500" />
              </motion.span>
              and <span className="text-[#0f172a] font-medium">passion</span>
            </p>
          </motion.div>

          <motion.button
            onClick={scrollToTop}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300"
            style={{
              background: 'rgba(0, 0, 0, 0.05)',
              border: '1px solid rgba(0, 0, 0, 0.1)',
              color: '#0f172a',
            }}
            onMouseEnter={e => {
              (e.currentTarget as HTMLElement).style.background = 'rgba(0, 0, 0, 0.1)';
            }}
            onMouseLeave={e => {
              (e.currentTarget as HTMLElement).style.background = 'rgba(0, 0, 0, 0.05)';
            }}
          >
            <span>Back to top</span>
            <ArrowUp size={14} />
          </motion.button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;